/**
 * Histórico de alterações dos itens de uma fatura (#341): quando, quem, o que mudou e por quê.
 * Recarrega quando `versao` muda, que é como a página avisa que acabou de corrigir um item.
 */
import React, { useEffect, useState } from 'react';
import { Box, Chip, CircularProgress, Typography } from '@mui/material';
import { History as HistoryIcon } from '@mui/icons-material';

import { DataTable, SectionCard, type DataTableColumn } from '../../components';
import {
  invoiceService,
  type InvoiceItemChange,
  type InvoiceItemSnapshot,
} from '../../services/invoiceService';
import { formatCurrency } from '../../utils/financialHelpers';

interface InvoiceItemChangesBlockProps {
  invoiceId: number;
  versao: number;
  accentColor: string;
}

function dataHora(valor: string): string {
  return new Date(valor).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Só o que mudou, uma linha por campo: "Quantidade: 1 → 3". */
function diferencas(antes: InvoiceItemSnapshot, depois: InvoiceItemSnapshot): string[] {
  const linhas: string[] = [];
  if (antes.description !== depois.description) {
    linhas.push(`Descrição: ${antes.description} → ${depois.description}`);
  }
  if (Number(antes.quantity) !== Number(depois.quantity)) {
    linhas.push(`Quantidade: ${antes.quantity} → ${depois.quantity}`);
  }
  if (Number(antes.unitPrice) !== Number(depois.unitPrice)) {
    linhas.push(
      `Preço unitário: ${formatCurrency(Number(antes.unitPrice))} → ${formatCurrency(Number(depois.unitPrice))}`
    );
  }
  if (Number(antes.subtotal) !== Number(depois.subtotal)) {
    linhas.push(
      `Subtotal: ${formatCurrency(Number(antes.subtotal))} → ${formatCurrency(Number(depois.subtotal))}`
    );
  }
  return linhas;
}

function oQueMudou(alteracao: InvoiceItemChange): string[] {
  if (alteracao.after) return diferencas(alteracao.before, alteracao.after);
  const { description, subtotal } = alteracao.before;
  return [`${description} (${formatCurrency(Number(subtotal))}) saiu da fatura`];
}

const columns: DataTableColumn<InvoiceItemChange>[] = [
  { id: 'createdAt', label: 'Quando', primary: true, render: (linha) => dataHora(linha.createdAt) },
  { id: 'userName', label: 'Quem', render: (linha) => linha.userName ?? '—' },
  {
    id: 'action',
    label: 'Ação',
    render: (linha) => (
      <Chip
        size='small'
        variant='outlined'
        color={linha.action === 'REMOVIDO' ? 'error' : 'info'}
        label={linha.action === 'REMOVIDO' ? 'Removido' : 'Editado'}
      />
    ),
  },
  {
    id: 'mudanca',
    label: 'Antes → depois',
    render: (linha) => (
      <Box>
        {oQueMudou(linha).map((texto) => (
          <Typography key={texto} variant='body2'>
            {texto}
          </Typography>
        ))}
      </Box>
    ),
  },
  { id: 'reason', label: 'Motivo', render: (linha) => linha.reason },
];

export const InvoiceItemChangesBlock: React.FC<InvoiceItemChangesBlockProps> = ({
  invoiceId,
  versao,
  accentColor,
}) => {
  const [alteracoes, setAlteracoes] = useState<InvoiceItemChange[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let ativo = true;
    invoiceService
      .getItemChanges(invoiceId)
      .then((lista) => {
        if (!ativo) return;
        setAlteracoes(lista);
        setErro(null);
      })
      .catch((err: unknown) => {
        if (ativo) {
          setErro(err instanceof Error ? err.message : 'Não foi possível carregar o histórico.');
        }
      })
      .finally(() => {
        if (ativo) setLoading(false);
      });
    return () => {
      ativo = false;
    };
  }, [invoiceId, versao]);

  const conteudo = (() => {
    if (loading) return <CircularProgress size={20} />;
    if (erro) {
      return (
        <Typography variant='body2' color='error'>
          {erro}
        </Typography>
      );
    }
    if (alteracoes.length === 0) {
      return (
        <Typography variant='body2' color='text.secondary'>
          Nenhuma alteração nos itens desta fatura.
        </Typography>
      );
    }
    return (
      <DataTable
        columns={columns}
        rows={alteracoes}
        getRowId={(linha) => linha.id}
        emptyState={{
          title: 'Nenhuma alteração nos itens desta fatura',
          description: 'Cada item editado ou removido aparece aqui, com quem alterou e o motivo.',
        }}
      />
    );
  })();

  return (
    <SectionCard
      title='Histórico de alterações'
      icon={<HistoryIcon fontSize='small' />}
      accentColor={accentColor}
      badge={<Chip size='small' label={alteracoes.length} />}
    >
      {conteudo}
    </SectionCard>
  );
};

export default InvoiceItemChangesBlock;
